import React from 'react';
import cn from 'classnames';
import moize from 'moize';
import PropTypes from 'prop-types';

import style from './PieChart.css';

import d3 from '../../utils/d3-import';
import hideSnap from '../../utils/hideSnap';
import { displayPercent } from '../../utils/Format/Format';

const pie = d3.pie().value((d) => d.value);

const getArc = moize.deep(
    (width, height, anglePadding, ringWidth, cornerRadius, margin) => {
        const radius = Math.min(width, height) / 2;
        const outerRadius = radius - margin;

        return d3
            .arc()
            .padAngle(anglePadding)
            .outerRadius(outerRadius)
            .innerRadius(outerRadius - ringWidth)
            .cornerRadius(cornerRadius);
    }
);

const getColor = moize.deep((data, colors) =>
    d3
        .scaleOrdinal()
        .domain(data.map((d) => d.label))
        .range(colors)
);

const getPieData = moize.deep((data) => pie(data));

/**
  Donut Chart
*/
class PieChart extends React.Component {
    static propTypes = {
        /**
          Padding of each slice angle
          useful for adding space between
          slices
        */
        anglePadding: PropTypes.number,
        /**
          segment data needed to render
          the chart
        */
        data: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string.isRequired,
                value: PropTypes.number.isRequired
            })
        ).isRequired,
        /**
          width of the pie chart
        */
        width: PropTypes.number,
        /**
          height the pie chart
        */
        height: PropTypes.number,
        /**
          function that formats the
          value for display
        */
        valueFormatter: PropTypes.func,
        /**
          array of colors in hex format
        */
        colors: PropTypes.arrayOf(PropTypes.string),
        /**
          border/corner radius
          of each arc segment
        */
        cornerRadius: PropTypes.number,
        /**
          Margin from the exterior
          of the svg chart to the ring
        */
        margin: PropTypes.number,
        /**
          width in pixels of the ring
        */
        ringWidth: PropTypes.number,
        /**
          boolean that represents
          whether the active segment label
          and value should be
          seen in the center text
        */
        showActiveLabel: PropTypes.bool,
        /**
          boolean which represents whether
          arc segments should be labeled
        */
        showLabels: PropTypes.bool
    };

    static defaultProps = {
        anglePadding: 0.01,
        width: 300,
        height: 300,
        valueFormatter: (x) => displayPercent(x),
        colors: [
            '#004b71',
            '#ffdf00',
            '#e37a00',
            '#c03123',
            '#614a7a',
            '#7c993f'
        ],
        cornerRadius: 2,
        margin: 10,
        ringWidth: 10,
        showActiveLabel: true,
        showLabels: false
    };

    constructor(props) {
        super(props);
        this.targetRef = React.createRef();

        this.state = { activeSegment: null };
    }

    get arc() {
        return getArc(
            this.props.width,
            this.props.height,
            this.props.anglePadding,
            this.props.ringWidth,
            this.props.cornerRadius,
            this.props.margin
        );
    }

    get color() {
        return getColor(this.props.data, this.props.colors);
    }

    get pieData() {
        return getPieData(this.props.data);
    }

    activateSegment(activeSegment) {
        this.setState({ activeSegment });
    }

    render() {
        const {
            data,
            height,
            margin,
            ringWidth,
            showActiveLabel,
            showLabels,
            title,
            valueFormatter,
            width
        } = this.props;

        const { activeSegment } = this.state;

        const activeSegmentData =
            activeSegment && data.find((d) => d.label === activeSegment).value;

        const showTitle = (!showActiveLabel || !activeSegment) && title;
        const showActiveSegment = showActiveLabel && activeSegment;

        return (
            <div
                className={cn('pie-chart', { 'has-active': activeSegment })}
                ref={this.targetRef}
            >
                <div
                    className="center-text"
                    style={{ width, height, padding: margin + ringWidth }}
                >
                    {showTitle && <span className="title">{title}</span>}
                    {showActiveSegment && (
                        <span className="active-segment-label">
                            {activeSegment}
                            <br />
                            <strong>{valueFormatter(activeSegmentData)}</strong>
                        </span>
                    )}
                </div>

                <svg width={width} height={height}>
                    <g transform={`translate(${width / 2},${height / 2})`}>
                        {this.pieData.map((slice) => (
                            <g
                                onMouseEnter={() =>
                                    this.activateSegment(slice.data.label)
                                }
                                onMouseLeave={() => this.activateSegment(null)}
                                key={`arc-${slice.data.label}`}
                                className={cn('arc', {
                                    active: activeSegment === slice.data.label
                                })}
                            >
                                <path
                                    d={this.arc(slice)}
                                    fill={this.color(slice.data.label)}
                                />
                                {showLabels && (
                                    <text
                                        transform={`translate(${this.arc.centroid(
                                            slice
                                        )})`}
                                        className="segment-label"
                                    >
                                        {slice.data.label}
                                    </text>
                                )}
                            </g>
                        ))}
                    </g>
                </svg>
                <style jsx>{style}</style>
            </div>
        );
    }
}

export default hideSnap(PieChart);
